import { ErrorCodes, VerbId as ApiVerbId } from '@tableau/api-internal-contract-js';
import { InternalTableauError } from '../InternalTableauError';
import { CommandRegistration } from './CommandRegistration';

/**
 * Registry which contains mappings from an Api VerbId to a pres-layer command.
 * This includes the parameters and return values of the command as well
 *
 * @export
 * @class CommandMappingRegistry
 */
export class CommandMappingRegistry {
  private _commands: { [apiVerb: string]: CommandRegistration };

  /**
   * Creates an instance of CommandMappingRegistry.
   * @memberof CommandMappingRegistry
   */
  public constructor() {
    this._commands = {};
  }

  /**
   * Adds a new command to the registry
   *
   * @param {CommandRegistration} command
   * @memberof CommandMappingRegistry
   */
  public addCommand(command: CommandRegistration): void {
    this._commands[command.apiVerbId] = command;
  }

  /**
   * Whether or not this registry contains a definition for this ApiVerbId
   *
   * @param {ApiVerbId} apiVerbId
   * @returns {boolean}
   * @memberof CommandMappingRegistry
   */
  public hasCommand(apiVerbId: ApiVerbId): boolean {
    if (!this._commands.hasOwnProperty(apiVerbId)) {
      return false;
    }

    if (!this._commands[apiVerbId]) {
      return false;
    }

    return true;
  }

  /**
   * Gets the command registration for this command id or throws if it doesn't exist
   *
   * @param {ApiVerbId} apiVerbId
   * @returns {CommandRegistration}
   * @memberof CommandMappingRegistry
   */
  public getCommand(apiVerbId: ApiVerbId): CommandRegistration {
    if (!this.hasCommand(apiVerbId)) {
      throw new InternalTableauError(ErrorCodes.UNKNOWN_VERB_ID, `Unknown ApiVerb: ${apiVerbId}`);
    }

    return this._commands[apiVerbId];
  }

  /**
   * @returns all the registered command ApiVerbId's as an array of strings
   */
  public getCommandMappingKeys(): Array<String> {
    return Object.keys(this._commands);
  }
}
